import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useState } from 'react';

import { IPaginationTable } from '../../table/common/interfaces/IPaginationTable';
import HaggleEventsAdminTable from '../../table/haggle-event/HaggleEventsAdminTable';
import filterSwitch from './utils/filterSwitch';

import LoadingSkeletonModalTable from '@/components/modal/prompt/LoadingSkeletonModalTable';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import {
	calculateStatus,
	formatHaggleEventDate,
	formatPrice,
} from '@/components/utils/utils';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { ICollectionHaggleEvent } from '@/interfaces/api/haggle-event/ICollectionHaggleEvent';
import { IHaggleEvent } from '@/interfaces/api/haggle-event/IHaggleEvent';
import { IHaggleWithAction } from '@/interfaces/api/haggle-event/IHaggleWithAction';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const HaggleEventPanel = () => {
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageNumber, setPageNumber] = useState(1);
	const [searchQuery, setSearchQuery] = useState('');
	const [filterSelected, setFilterSelected] = useState('');

	const filter = {
		'page[size]': pageSize,
		'page[number]': pageNumber,
		...(searchQuery && { 'filterQuery[findByIdOrTradeName]': searchQuery }),
		...(filterSelected && filterSwitch(filterSelected)),
	};

	const {
		data: haggleEvents,
		isLoading: haggleEventIsLoading,
		isError: haggleEventIsError,
		error: haggleEventError,
		refetch: haggleEventRefetch,
	} = useQuery<ICollectionHaggleEvent>({
		queryKey: [ApiQueryKeys.getAllHaggleEventsAdmin, pageSize, pageNumber],
		queryFn: () =>
			haggleApiService.getAllHaggleEventsAdmin({
				params: filter,
			}),
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		haggleEventRefetch();
	}, [filterSelected, haggleEventRefetch]);

	const { data: merchants } = useQuery({
		queryKey: [ApiQueryKeys.getAllMerchants],
		queryFn: () => haggleApiService.getAllMerchants(),
		refetchOnWindowFocus: false,
	});

	if (haggleEventIsError) {
		if (haggleEventError instanceof Error) {
			notificationService.error(haggleEventError.message);
		} else {
			notificationService.error('Unknown error fetching haggle events');
		}
	}

	const handlePaginationOptions = (
		collection: ICollectionHaggleEvent,
	): IPaginationTable => {
		return {
			pageSize,
			pageNumber,
			pageCount: collection.pageCount,
			itemCount: collection.itemCount,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize,
			setPageNumber,
		};
	};

	const columnHelper = createColumnHelper<IHaggleWithAction>();

	const columns = [
		columnHelper.accessor('store.tradeName', {
			cell: (info) => <div className="w-[150px]">{info.getValue()}</div>,
			header: () => <div className="w-[150px]">STORE NAME</div>,
		}),
		columnHelper.accessor('id', {
			cell: (info) => <div className="w-[50px]">{info.getValue()}</div>,
			header: () => <div className="w-[50px]">ID</div>,
		}),

		{
			id: 'status',
			cell: (info: { row: { original: IHaggleEvent } }) => {
				const haggleEvent = info.row.original;
				return (
					<div className="w-[70px]">{`${calculateStatus(
						haggleEvent.endDate,
						haggleEvent.startDate,
					)}`}</div>
				);
			},
			header: () => <div className="w-[70px]">STATUS</div>,
		},
		columnHelper.accessor('startDate', {
			cell: (info) => (
				<div className="w-[170px]">
					{formatHaggleEventDate(
						info.row.original.startDate,
						info.row.original.endDate,
					)}
				</div>
			),
			header: () => <div className="w-[170px]">DATE</div>,
		}),
		columnHelper.accessor('chatBots', {
			cell: (info) => {
				const agreementsReached = info
					.getValue()
					.filter((chatBot) => chatBot.agreement).length;
				return (
					<div className="w-[120px] text-center">{`${agreementsReached}/${
						info.getValue().length
					}`}</div>
				);
			},
			header: () => <div className="w-[120px] text-center">DEALS/CHATS</div>,
		}),
		columnHelper.accessor('acceptableMargin', {
			cell: (info) => (
				<div className="w-[80px] text-center">{`${info.getValue()} %`}</div>
			),
			header: () => <div className="w-[80px] text-center">MAX. DISCOUNT</div>,
		}),
		columnHelper.accessor('totalSalesMade', {
			cell: (info) => (
				<div className="w-[120px] text-center">{`${formatPrice(
					info.getValue() ?? '0',
				)}`}</div>
			),
			header: () => <div className="w-[120px] text-center">TOTAL SALES</div>,
		}),
		columnHelper.accessor('totalProfit', {
			cell: (info) => (
				<div className="w-[210px] text-center">{`${formatPrice(
					info.getValue() ?? '0',
				)}`}</div>
			),
			header: () => (
				<div className="w-[210px] text-center">ADDED PROFIT MARGIN</div>
			),
		}),
		columnHelper.accessor('productIds', {
			cell: (info) => (
				<div className="w-[100px] text-center">{`${
					info.getValue().length
				}`}</div>
			),
			header: () => <div className="w-[100px]">PRODUCTS</div>,
		}),
	];

	return (
		<div
			className="min-h-content overflow-y-auto"
			data-test="haggle-events-admin-dashboard"
		>
			{haggleEventIsLoading && <LoadingSkeletonModalTable />}
			{haggleEvents && !haggleEventIsLoading && (
				<div className="flex flex-col h-full grow gap-4">
					<HaggleEventsAdminTable
						collectionHaggleEvent={haggleEvents}
						paginationOption={handlePaginationOptions(haggleEvents)}
						columns={columns}
						merchants={merchants ? merchants.itemCount : NaN}
						refetch={haggleEventRefetch}
						query={searchQuery}
						setQuery={setSearchQuery}
						setFilterSelected={setFilterSelected}
					/>
				</div>
			)}
		</div>
	);
};

export default HaggleEventPanel;
